// The grounded facts an envelope may carry, and the bound that keeps a
// pathological set off the wire. See `../task-envelope.ts` for the file docblock.

import { z } from 'zod';

/**
 * The `grounding` caps. Sized so the FIELD is a superset of what the scout survey
 * can emit today (`apps/runner/src/modes/scout/survey/finding.ts`: `SCOUT_CLAIM_MAX_CHARS`
 * 400, `CodeRefSchema` 420, `SCOUT_MAX_REFS` 16) rather than a second copy of those
 * numbers. Same two-sided posture as `BRIEF_FIELD_MAX` in
 * `apps/runner/src/harness/prompt.ts`: the producer's schema bounds what a scout may
 * SAY, these bound what the envelope will CARRY, and {@link boundedGrounding} is what
 * keeps a widening on either side from becoming a parse failure at the mint.
 *
 * WHAT THEY COST, measured rather than assumed. The grounding is plan-wide, so a
 * 12-group plan carries it 12 times on the ONE `plan_authored` message
 * (`fleet.evt`, `EVENTS_STREAM_MAX_MSG_BYTES` = 8 MB). Worst case at these caps is
 * 40 x (500 + 6 x 500) = 140 KB per envelope, 1.7 MB for a full plan — inside the
 * stream limit with room to spare. Realistic case, from the 27 prod surveys that
 * produced anchors (~41 verified paths each): ~11 KB per envelope. Today's
 * `plan_authored` payloads run 1.3 KB average, 14 KB peak.
 */
export const GROUNDING_MAX_FACTS = 40;
export const GROUNDING_CLAIM_MAX = 500;
export const GROUNDING_REFS_MAX = 6;
export const GROUNDING_REF_MAX = 500;

/**
 * ONE thing the scout established while it had the repo open: a claim, plus the
 * `path:line` anchors it actually opened to establish it.
 *
 * The refs are NOT re-validated as `path:line` here, and that is deliberate: the
 * shape gate belongs to the producer (`CodeRefSchema`, `modes/scout/survey/finding.ts`),
 * and the checkout half — probing each anchor against the real checkout
 * (`repoAnchorProbe`, `modes/scout/survey/anchors.ts`) — belongs there too, and a
 * second parser here would be a second opinion about what an anchor is with no
 * checkout to settle it. What this schema owns is the BOUND — how long a citation may
 * be and how many ride one claim — because that is the part the envelope pays for.
 */
export const GroundedFactSchema = z.object({
  claim: z.string().min(1).max(GROUNDING_CLAIM_MAX),
  refs: z.array(z.string().min(1).max(GROUNDING_REF_MAX)).min(1).max(GROUNDING_REFS_MAX),
});
export type GroundedFact = z.infer<typeof GroundedFactSchema>;

/**
 * Bring a scout's anchored facts inside the {@link GroundedFactSchema} caps.
 *
 * TRUNCATES — and the contrast with its neighbour {@link provableFilesTouched} is
 * the entire reason this is its own function rather than a second caller of that
 * one. `files_touched` is a CONCURRENCY CONTRACT: a set missing one path is not a
 * smaller claim, it is a false one, and the failure is two boxes editing one file.
 * Grounding is EVIDENCE — read-only citations the coding agent may open, fenced by
 * nothing and enforcing nothing — so a shorter list of citations is exactly a
 * shorter list of citations. Refusing all 40 because the 41st was one character too
 * long would throw away 40 good citations to protect nothing.
 *
 * The one thing it will NOT do is keep a claim with no anchor left. An unanchored
 * claim is not a fact — that is `ScoutFactSchema`'s own `refs.min(1)` rule — and a
 * coding agent handed one has model prose it cannot check and no way to know that
 * is what it is.
 *
 * De-dupe runs BEFORE the cap for the same reason it does in
 * {@link provableFilesTouched}: collapsing a repeat shortens the list without
 * dropping anything — and a repeated CLAIM therefore MERGES its anchors rather
 * than dropping the later fact whole (#2917 review). `surveyGrounding` walks
 * every finding of every scout in one survey, so two scouts reaching the same
 * conclusion from different files is the ordinary shape, not the exotic one;
 * skipping the second fact would have thrown away the second scout's anchor and
 * left the sentence above asserting a guarantee this function no longer gave.
 * The ref cap applies to the MERGED set, so a claim still carries at most
 * {@link GROUNDING_REFS_MAX} anchors.
 *
 * The fact cap bounds DISTINCT claims: once {@link GROUNDING_MAX_FACTS} of them
 * are held, a further NEW claim is refused while a repeat of one already held
 * still merges. Refusing rather than breaking out is what keeps merging total —
 * an early exit would make a claim's anchor set depend on how far down the input
 * its twin happened to sit.
 *
 * Pure.
 */
export function boundedGrounding(
  facts: readonly { readonly claim: string; readonly refs: readonly string[] }[] | null | undefined,
): GroundedFact[] {
  if (facts == null) return [];
  // Insertion-ordered, so the output keeps first-appearance order of each claim.
  const byClaim = new Map<string, string[]>();
  for (const fact of facts) {
    const claim = fact.claim.trim();
    if (claim === '' || claim.length > GROUNDING_CLAIM_MAX) continue;
    const held = byClaim.get(claim);
    if (held === undefined && byClaim.size >= GROUNDING_MAX_FACTS) continue;
    const refs = held ?? [];
    for (const raw of fact.refs) {
      if (refs.length >= GROUNDING_REFS_MAX) break;
      const ref = raw.trim();
      // `includes` over a set: `refs` is capped at 6, so the scan is cheaper than
      // the parallel Set it would replace, and there is one place a ref lives.
      if (ref === '' || ref.length > GROUNDING_REF_MAX || refs.includes(ref)) continue;
      refs.push(ref);
    }
    // A claim whose anchors were ALL unusable is not a fact and must not consume
    // one of the 40 slots either — same rule the unanchored case always had.
    if (held === undefined && refs.length > 0) byClaim.set(claim, refs);
  }
  return [...byClaim].map(([claim, refs]) => ({ claim, refs }));
}
